import { COMMON_COLOR_SCHEME as scheme, COLORS, CHART_COLORS } from './colors';

import zindex from '../../../zindexes';

const FONT_SIZE = {
    XS: 3,
    SMALL: 6,
    MEDIUM: 12,
    LARGE: 16,
    XL: 24
};
const SPACING = {
    XS: 3,
    SMALL: 6,
    MEDIUM: 12,
    LARGE: 16,
    XL: 24
};

/**
 *
 * @param {import('../../styling').ThemeParameters} params
 * @returns {import('../../styling').BackboneTheme}
 */
export function themeValues(params) {
    const {
        darkMode = false,
        mobile = false,
        tablet = false,
        width,
        height,
        smallMobile,
        keyboardHeight = 0,
        isIos = false,
        ...themeOverrides
    } = params;
    /** @type {import('../../styling').BackboneTheme} */
    let theme = {
        __title: 'old',
        // basic indicators:
        // @ts-ignore
        native: __SST_REACT_NATIVE__ || __TEST_RFID__,
        viewPort: mobile ? 'MOBILE' : tablet ? 'TABLET' : 'DESKTOP',
        tablet,
        mobile,
        smallMobile,
        isIos,
        // should not be used directly outside of the themeValues function
        darkMode,

        // passed parameters that might be useful for component rendering
        height,
        width,
        keyboardHeight,

        // the actual theme values:
        zindex,
        iconSize: 20,
        colorScheme: scheme,
        activeBorderColor: scheme.primary,
        selectedBorderColor: darkMode ? scheme['border-neutral'] : scheme.border,
        borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        baseBackgroundColor: darkMode ? '#000' : scheme.background,
        baseBackgroundColorDarker: darkMode ? '#333' : '#ccc',
        // with each layer, progressively lightens/darkens the parent or baseBackgroundColor
        backgroundColorLighter: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
        backgroundColorDarker: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        backgroundColorOnDarkColors: darkMode ? 'rgb(65, 65, 65)' : 'rgb(187, 187, 187)',
        backgroundHint: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        disabledBackgroundColor: scheme['disabled-background'],
        defaultFontColor: darkMode ? scheme['white-text'] : COLORS.trueBlack,
        invertedFontColor: darkMode ? COLORS.trueBlack : scheme['white-text'],
        disabledFontColor: darkMode ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
        lighterDisabledFontColor: darkMode ? 'rgba(255, 255, 0, 0.55)' : 'rgba(0, 0, 0, 0.25)',
        fontColorForDarkBackground: scheme['white-text'],
        fontColorForLightBackground: COLORS.trueBlack,
        errorFontColor: scheme.error,
        unobtrusiveFontColor: darkMode ? '#bbbbbb' : '#444444',
        subheadingFontColor: darkMode ? scheme['light-accent-text'] : scheme['dark-accent-text'],
        font: 'Roboto',
        fontAlternate: 'Nunito',
        fontSizeSmall: FONT_SIZE.MEDIUM,
        fontSize: FONT_SIZE.LARGE,
        fontSizeLarge: FONT_SIZE.XL,
        fontSizeLabel: FONT_SIZE.MEDIUM,
        disabledColor: scheme['disabled-color'],
        drawerWidth: mobile ? width : 240,
        gridLineColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        listLineHeight: 3 * FONT_SIZE.MEDIUM,
        listActiveBackgroundColor: darkMode ? '#D0EDF866' : scheme['accent-1-highlight'],
        listSelectedBackgroundColor: darkMode ? '#D0EDF866' : scheme['accent-1-highlight'],
        columnHeaderResizeHandle: darkMode ? '#D0EDF866' : '#ffffff50',
        columnWidthToHeightRatio: 0.66,
        progressBarColor: scheme['primary'],
        borderRadius: SPACING.XS,
        viewPadding: SPACING.SMALL,
        viewPaddingMore: SPACING.MEDIUM,
        viewMargin: SPACING.SMALL,
        viewMarginMore: SPACING.MEDIUM,
        textMargin: mobile ? SPACING.XS : SPACING.SMALL,
        textPadding: SPACING.SMALL,
        textPaddingMore: SPACING.MEDIUM,
        maxTableColumnWidth: 200,
        maxTableCharLength: 255,
        spinnerColor: darkMode ? scheme['white-text'] : scheme['primary'],
        icon: {
            font: 'material-icons',
            fontAlternate: 'MaterialCommunityIcon',
            size: FONT_SIZE.LARGE,
            error: scheme.error,
            warning: scheme.warn,
            warn: scheme.warn
        },
        caretColor: darkMode ? scheme['white-text'] : scheme['black-text'],
        labelColor: darkMode ? scheme['white-text'] : scheme['black-text'],
        labelForEmptyField: darkMode ? scheme['light-action-available'] : scheme['dark-action-available'],
        linkColor: darkMode ? COLORS.lightBlue : COLORS.blue,
        linkFontSize: FONT_SIZE.MEDIUM,
        disabledLabelColor: darkMode ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
        mainContainer: {
            margin: mobile ? 0 : SPACING.SMALL,
            height: height - 60 - SPACING.SMALL - 4,
            padding: SPACING.LARGE,
            gap: SPACING.LARGE,
            // something somehow is not working on the old UI
            // and the maincontainer is 6 px too far to the right...
            offset: 6
        },
        modal: {
            minWidth: mobile ? width : 300,
            /* For whatever reason, 90% does not yield the same value as width * 0.9 */
            maxWidth: mobile ? width : width * 0.9,
            minHeight: mobile ? height : 0,
            backgroundColor: darkMode ? 'rgb(51, 51, 51)' : 'rgb(204, 204, 204)',
            backgroundFade: 'rgba(0, 0, 0, 0.6)',
            padding: 0,
            margin: SPACING.MEDIUM,
            borderRadius: mobile ? 0 : SPACING.XS,
            boxShadow: darkMode
                ? '8px 11px 13px -2px #000000f0, 4px 23px 39px 0px #5787a9c0'
                : '4px 4px 11px -2px #000000d0, 4px 16px 32px 4px #5787a970',
            font: {
                fontFamily: 'Arial'
            },
            header: {
                padding: SPACING.MEDIUM,
                gap: 0
            },
            body: {
                padding: 16
            },
            progressDialog: {
                headerColor: scheme['white-text'],
                padding: mobile ? SPACING.MEDIUM : SPACING.LARGE,
                margin: SPACING.SMALL
            },
            columnCustomization: {
                padding: 6
            },
            native: {
                elevation: 10
            }
        },
        button: {
            fontSize: FONT_SIZE.LARGE,
            // If darkMode uses lighter colors, then invert the text for contrast
            //fontColor: darkMode ? scheme['black-text'] : scheme['white-text'],
            fontColor: scheme['white-text'],

            height: 2 * FONT_SIZE.LARGE,
            maxHeight: 40,
            maxWidth: 200,
            minWidth: 2 * SPACING.LARGE,

            borderRadius: SPACING.XS,
            roundDiameter: 2 * FONT_SIZE.LARGE,
            roundDiameterLarge: 3 * FONT_SIZE.LARGE + 2 * SPACING.XS,

            padding: 8,
            primary: scheme['primary'],
            primaryHover: scheme['primary-hover'],
            primaryHighlight: scheme['primary-highlight'],
            success: scheme['success'],
            successHover: scheme['success-hover'],
            successHighlight: scheme['success-highlight'],
            warn: scheme.warn,
            warnHover: scheme['warn-hover'],
            warnHighlight: scheme['warn-highlight'],
            error: scheme.error,
            errorHover: scheme['error-hover'],
            errorHighlight: scheme['error-highlight'],
            negative: scheme.error,
            negativeHover: scheme['error-hover'],
            negativeHighlight: scheme['error-highlight'],
            transparent: '#00000000',
            transparentHover: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            transparentHighlight: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            base: darkMode ? '#000' : scheme.background,
            baseHover: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            baseHighlight: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            accentOne: scheme['accent-1'],
            accentOneHover: scheme['accent-1-hover'],
            accentOneHighlight: scheme['accent-1-highlight'],
            accentTwo: scheme['accent-2'],
            accentTwoHover: scheme['accent-2-hover'],
            accentTwoHighlight: scheme['accent-2-highlight'],
            // For pseudo-disabled components that still require interaction (like notifications button)
            gray: COLORS.darkGray,
            grayHover: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            grayHighlight: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
        },
        card: {
            minWidth: 280,
            backgroundColor: darkMode ? '#000' : scheme.background,
            borderRadius: 0,
            boxShadow: darkMode
                ? '0 2px 2px 0 rgb(255 255 255 / 14%), 0 3px 1px -2px rgb(255 255 255 / 20%), 0 1px 5px 0 rgb(255 255 255 / 12%)'
                : '0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)',
            // Header matches selected navHeadings color scheme and header colors from UI v1
            // Don't worry, looks fine on darkmode
            header: {
                height: 50,
                textColor: scheme['white-text'],
                backgroundColor: scheme['background-dark'],
                padding: `0 ${SPACING.LARGE}px`,
                font: {
                    fontSize: 32,
                    fontFamily: 'Nunito'
                }
            },
            body: {
                border: '1px solid ' + (darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'),
                padding: SPACING.LARGE,
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
            },
            footer: {
                backgroundColor: 'transparent',
                padding: `8px ${SPACING.LARGE}px`,
                border: '1px solid ' + (darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'),
                borderColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
            }
        },
        cardList: {
            padding: 0,
            backgroundColor: darkMode ? '#000' : scheme['white-text']
        },
        charts: {
            legendNavActive: darkMode ? scheme['white-text'] : scheme['black-text'],
            legendHoverColor: darkMode ? scheme['primary-hover'] : scheme['primary'],
            legendBackgroundColor: darkMode ? '#00000090' : '#ffffff90',
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            boxShadow: 'unset',
            header: {
                font: {
                    fontSize: 16,
                    fontFamily: 'Nunito'
                },
                padding: '0px 16px',
                gap: 16,
                titleMargin: '12px 0px'
            }
        },
        chartColors: darkMode ? CHART_COLORS.dark : CHART_COLORS.light,
        dataAggregation: {
            fontColor: darkMode ? scheme['primary-hover'] : scheme.primary
        },
        form: {
            // These pseudo-static sizes are used for FormField components (which encapsulate
            // most form data entry controls).
            // This seems to work well with tested useCases, but it is possible that
            // additional nesting of fieldsets within fieldsets might create less
            // aesthetically pleasing results and a more dynamic approach might be needed.
            entryMinWidth: Math.min(280, width - 30),
            entryMaxWidth: Math.min(350, width - 30),
            inputBorderRadius: SPACING.XS,
            dropdownMinHeight: 200,
            sectionGap: SPACING.MEDIUM,
            labelFont: {
                fontFamily: 'Roboto'
            },
            textFont: {
                fontFamily: 'Roboto'
            },
            buttonBar: {
                minHeight: 32,
                justifyContent: 'flex-end',
                margin: 6,
                boxShadow: 'none'
            }
        },
        menu: {
            borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: SPACING.XS,
            backgroundColor: darkMode ? 'rgb(51, 51, 51)' : 'rgb(204, 204, 204)',
            padding: 0,
            gap: 0,
            menuItem: {
                padding: 16,
                hoverBackgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
            },
            divider: {
                color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                margin: '0.5rem 0'
            },
            subMenu: {
                chevronSize: 10,
                chevronRightOffset: '1rem'
            },
            header: {
                padding: `${SPACING.SMALL}px ${SPACING.MEDIUM}px ${SPACING.SMALL}px ${SPACING.SMALL}px`,
                gap: SPACING.SMALL
            },
            footer: {
                color: darkMode ? '#bbbbbb' : '#444444'
            },
            native: {
                marginTop: 52, // the hight of the title bar
                paddingTop: SPACING.SMALL,
                paddingLeft: SPACING.SMALL,
                shell: {
                    marginLeft: SPACING.LARGE,
                    paddingTop: SPACING.SMALL,
                    legacyPadding: SPACING.LARGE
                },
                subMenu: {
                    // marginLeft is not used in the old theme
                    paddingLeft: SPACING.LARGE
                },
                divider: {
                    margin: '.5rem'
                },
                versionFooter: {
                    marginLeft: SPACING.LARGE,
                    marginTop: SPACING.SMALL
                }
            }
        },
        titleBar: {
            bottomBorderWidth: 3,
            borderColor: '#2A2B30',
            height: 60,
            titleGap: SPACING.SMALL,
            marginLeft: 'auto',
            marginRight: SPACING.MEDIUM,
            color: scheme['white-text'],
            backgroundColor: scheme['background-medium']
        },
        sideBar: {
            backgroundColor: scheme['background-medium'],
            logoMaxHeight: '40px',
            logoMaxWidth: '100%'
        },
        outerMenus: {
            backgroundColor: scheme['background-medium'],
            border: scheme['border'],
            borderWidth: 4,
            chevronColor: scheme['white-text'],
            chevronColorHover: scheme['white-text'],
            fontColor: scheme['white-text'],
            fontColorHover: scheme['white-text'],
            fontColorActive: scheme['white-text'],
            disabledBackgroundColor: 'rgba(0, 0, 0, 0.2)',
            disabledFontColor: 'rgba(255, 255, 255, 0.45)',
            margin: SPACING.MEDIUM,
            subMenuOffset: 30,
            navHeaderBackground: 'transparent',
            navHeaderBorder: 'transparent',
            navHeaderBackgroundHover: scheme['background-dark'],
            navHeaderBackgroundActive: scheme['background-dark'],
            navHeaderBorderHover: scheme['background-dark'],
            navHeaderDisabledBackground: 'transparent',
            navHeaderDisabledBorder: 'transparent',
            navHeaderLinkBackground: scheme['background-dark'],
            navHeaderLinkBorder: scheme['background-dark'],
            navHeaderLinkBackgroundHover: scheme['background-dark'],
            navHeaderLinkBorderHover: scheme['background-dark'],
            navHeaderLinkBackgroundActive: scheme['background-dark'],
            navHeaderLinkBorderActive: scheme['background-dark']
        },
        slider: {
            selected: darkMode ? scheme['primary'] : scheme['primary-hover'],
            hover: darkMode ? scheme['primary-hover'] : scheme['primary'],
            backgroundColor: darkMode ? scheme['disabled-background'] : scheme['background-light'],
            disabled: darkMode ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)'
        },
        tabs: {
            scrollbar: {
                width: 8,
                height: 8,
                borderRadius: 9999,
                marginTop: 2,
                marginBottom: 2
            }
        },
        rangeFieldContainer: {
            margin: '0 4px',
            titleMargin: 8,
            formField: {
                paddingBottom: 8,
                marginBottom: 0
            }
        },
        searchPane: {
            rangeSearchElement: {
                margin: '16px 10px 16px 4px',
                borderRadius: 10
            },
            multiPickerDropdown: {
                marginTop: 0
            },
            formField: {
                marginRight: 6,
                marginBottom: 0
            }
        },
        swagger: {
            backgroundColor: COLORS.white
        },
        switch: {
            track: {
                height: {
                    standard: {
                        sm: 25
                    },
                    multistate: {
                        sm: 25
                    }
                },
                width: {
                    standard: {
                        sm: 46
                    },
                    multistate: {
                        sm: 46
                    }
                },
                color: {
                    unchecked: COLORS.paleRed,
                    checked: COLORS.paleGreen, // darkMode ? scheme['disabled-background'] : scheme['primary-highlight'],
                    intermediate: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    disabled: scheme['disabled-background']
                },
                borderRadius: 31,
                margin: {
                    sm: {
                        right: SPACING.SMALL,
                        left: SPACING.SMALL
                    }
                },
                boxShadow: 'inset 0 0 2px #5c5c60'
            },
            thumb: {
                height: {
                    sm: 21
                },
                width: {
                    sm: 21
                },
                color: {
                    checked: scheme['primary'],
                    unchecked: darkMode ? scheme['primary-hover'] : scheme['dark-action-available'],
                    disabled: scheme['disabled-color']
                },
                position: {
                    sm: {
                        left: 2,
                        bottom: 2
                    }
                },
                transform: {
                    standard: {
                        sm: {
                            unchecked: 0,
                            checked: 20
                        }
                    },
                    multistate: {
                        sm: {
                            unchecked: 0,
                            checked: 20,
                            intermediate: 10
                        }
                    }
                }
            },
            options: {
                color: {
                    selected: darkMode ? scheme['white-text'] : COLORS.trueBlack,
                    unselected: darkMode ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)'
                },
                font: {
                    sm: {
                        selected: {
                            fontSize: undefined
                        },
                        unselected: {
                            fontSize: undefined
                        }
                    }
                }
            }
        },
        table: {
            headerColor: scheme['background-dark'],
            cellHorizontalPaddingInPx: 0
        },
        layout: {
            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
            backgroundColorDarker: darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
        },
        blocklyEditor: {
            menuEditorPadding: 4,
            toggleGroupMargin: 6,
            toggleSize: 32,
            accessModifier: {
                size: 16,
                green: COLORS.green,
                red: COLORS.redbeamRed,
                white: COLORS.white,
                margin: 5,
                noIconSize: 16
            }
        },
        userActivatedInput: {
            containerWidth: 300
        },
        ...themeOverrides
    };
    if (mobile) {
        theme = {
            ...theme,
            listLineHeight: 50,
            borderRadius: 4,
            iconSize: 2 * FONT_SIZE.LARGE,
            button: {
                ...theme.button,
                roundDiameter: 3 * FONT_SIZE.LARGE,
                roundDiameterLarge: 56,
                roundBorderRadius: 28, // % doesn't work on react native
                height: 4 * FONT_SIZE.LARGE,
                minWidth: 4 * FONT_SIZE.LARGE,
                // Remove hover states for mobile.
                successHover: undefined,
                primaryHover: undefined,
                errorHover: undefined,
                warnHover: undefined
            },
            card: {
                ...theme.card,
                padding: SPACING.SMALL
            },
            form: {
                ...theme.form,
                entryMinWidth: Math.min(smallMobile ? 175 : 280, width - 48),
                dropdownMinHeight: '100%'
            },
            outerMenus: {
                ...theme.outerMenus,
                margin: 0
            }
        };
    }

    return theme;
}

export const displayName = 'Classic';
